// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type ShipmentPaginated, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Button, LoadingOverlay } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import type { EntityState } from 'components/Mantine/StatusEditor/types';
import { useCustomStatuses } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import type { EntityStateAgGridContext } from 'helpers/ag-grid/EntitySetFilterComponent';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { isNil } from 'helpers/isNotNil';
import { useShipmentStatuses } from 'hooks/typeCacheHooks/useShipmentStatuses';

import { useShipmentModals, ShipmentModalProvider } from '../modalRegistries';
import { getShipmentColDef } from './columnDefs';

export type ShipmentsPageProps = {
  shipmentStates: EntityState<'Shipment'>[];
};

const ShipmentsTable = ({ shipmentStates }: ShipmentsPageProps) => {
  const { stateColorMap } = useCustomStatuses('Shipment');
  const { facilityIds, projectIds } = useGlobalFilters({
    facilities: true,
    projects: true,
  });

  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          facilityIds,
          projectIds,
        },
      },
    }),
    [facilityIds, projectIds],
  );

  const {
    fetchPage: fetchShipments,
    setDefaultOpts: setShipmentsDefaultOpts,
    abortAll: abortShipments,
  } = useEvolvePaginatedGet('shop/shipment', defaultOpts);

  useEffect(() => {
    setShipmentsDefaultOpts(defaultOpts);
  }, [defaultOpts, setShipmentsDefaultOpts]);

  const colDef = useMemo(() => getShipmentColDef(), []);
  const context = useMemo<EntityStateAgGridContext<'Shipment'>>(
    () => ({
      entityStates: shipmentStates,
      stateLabelMap: stateColorMap,
    }),
    [shipmentStates, stateColorMap],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'shipping-orders',
    tableViewType: TABLE_VIEW_TYPE.SHIPPING_ORDERS,
    exportFileName: 'Shipping Orders',
    colDef,
    fetchPage: fetchShipments,
    abortAll: abortShipments,
    rowId: 'shipmentId',
    context,
  });

  const { openModal } = useShipmentModals();

  return (
    <>
      <BasePageHeader
        title="Shipping Orders"
        topRightComponent={
          <Button leftSection={<EvolveIcon icon="Add" size="sm" />} onClick={() => openModal('NewShipmentModal')}>
            New shipping order
          </Button>
        }
      />
      <AgGridContainer>
        {(applyWrap) => (
          <AgGridReact<ShipmentPaginated>
            {...agGridProps}
            onFirstDataRendered={applyWrap}
            noRowsOverlayComponent={() => (
              <NoRowsOverlay label="No shipping orders found." icon="WorkOrder" gridApi={gridApi} />
            )}
          />
        )}
      </AgGridContainer>
    </>
  );
};

export const ShipmentsPage = () => {
  const { states, loading } = useShipmentStatuses();

  if (loading || isNil(states)) {
    return <LoadingOverlay visible />;
  }

  return (
    <ShipmentModalProvider>
      <ShipmentsTable shipmentStates={states} />
    </ShipmentModalProvider>
  );
};
